import { useEffect, useRef, useState } from 'react'
import { Copy } from '@stokr/components-library'
import { Section } from 'components/sto/Section'

import { CopyIcon, ExternalLinkIcon } from 'components/ui/icons'
import { ActionRow, ContactButton, CopyEmailButton, Description } from './Support.styles'
import { useStoTrack } from 'hooks/sto/useStoTracking'

const SUPPORT_URL = 'https://support.stokr.io/'
const SUPPORT_EMAIL = 'support@stokr.io'
const COPIED_RESET_MS = 2000

/**
 * Static — same copy and contact details for every project, so unlike the other
 * sections this has no data hook.
 */
/** `mirror`: the copy for the other width (mobile, after Key information), without the anchor id. */
export const Support = ({ mirror = false }: { mirror?: boolean }) => {
  const [copied, setCopied] = useState(false)
  const trackSto = useStoTrack()
  const resetTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined)

  // Clears the pending "reset to email" timer if the section unmounts mid-countdown.
  useEffect(() => () => clearTimeout(resetTimer.current), [])

  const handleCopyEmail = () => {
    trackSto('sto_support_clicked', { location: 'support', channel: 'copy_email' })
    // `navigator.clipboard` is undefined in insecure contexts and some older browsers —
    // calling `.writeText` on it throws synchronously, before any promise exists to
    // `.catch`. Guard first so an unsupported browser just no-ops instead of crashing
    // the click handler.
    if (!navigator.clipboard) return

    navigator.clipboard
      .writeText(SUPPORT_EMAIL)
      .then(() => {
        setCopied(true)
        clearTimeout(resetTimer.current)
        resetTimer.current = setTimeout(() => setCopied(false), COPIED_RESET_MS)
      })
      .catch(() => {})
  }

  return (
    <Section
      anchor="support"
      mirror={mirror}
      title={<Copy id="stoPage.support.title" />}
      state={{ status: 'ready', data: null }}>
      {() => (
        <>
          <Description>
            <Copy id="stoPage.support.descriptionLine1" />
            <br />
            <Copy id="stoPage.support.descriptionLine2" />
          </Description>

          <ActionRow>
            <ContactButton
              as="a"
              black
              href={SUPPORT_URL}
              target="_blank"
              rel="noreferrer"
              data-cy="sto-support-contact"
              onClick={() => trackSto('sto_support_clicked', { location: 'support', channel: 'help_center' })}>
              <Copy id="stoPage.support.contactUs" />
              <ExternalLinkIcon width={12} height={12} aria-hidden="true" />
            </ContactButton>

            {/* aria-live: the label swaps to "Copied" on click — without this, that
                change is silent to screen readers even though sighted users see it. */}
            <CopyEmailButton
              type="button"
              data-cy="sto-support-copy-email"
              onClick={handleCopyEmail}
              aria-live="polite">
              {copied ? <Copy id="stoPage.support.emailCopied" /> : SUPPORT_EMAIL}
              <CopyIcon width={12} height={12} aria-hidden="true" />
            </CopyEmailButton>
          </ActionRow>
        </>
      )}
    </Section>
  )
}
